import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  TextField,
  MenuItem,
  Avatar,
  Chip,
  Tooltip,
} from "@mui/material";

import { useAuth } from "context/authContext";
import {
  getJobs,
  getJobCompetencyMatrix,
  downloadCompToEmployeeEvidenceData,
  addAdminCompetencyEvaluationComment,
  resetCompAssessment,
} from "services/api";
import MDBox from "components/MDBox";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import LoadingScreen from "components/Loading";
import EmployeePointsDialog from "layouts/user/Competencies/assess/components/EmployeePointsDialog";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { Download } from "@mui/icons-material";
import EvidenceCommentButton from "components/EvidenceCommentButton";
import CommentButton from "components/CommentButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmationDialog from "components/ConfirmationDialog";

const CompAssessments = () => {
  const [loading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const { t } = useTranslation();
  const [selectedJob, setSelectedJob] = useState(null);
  const months = t("navigationBar.months", { returnObjects: true });
  const years = [2024, 2025, 2026, 2027];
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const snackbarRef = useRef();
  const [data, setData] = useState([]); // Grouped data
  const [loadingData, setLoadingData] = useState(false);
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [selectedEmployeeForPoints, setSelectedEmployeeForPoints] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { employee: me } = useAuth();
  const [myId, setMyId] = useState(null);
  const [resetCompDialogOpen, setResetCompDialogOpen] = useState(false);
  const [compToReset, setCompToReset] = useState(null); // Will store the compToEmpId

  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const jobsData = await getJobs();

        if (jobsData && jobsData !== "error") {
          setJobs(jobsData);
        } else {
          setJobs([]);
        }
        if (me?.id) {
          setMyId(me.id);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobs([]);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedJob?.jobId) {
        setLoadingData(true);
        try {
          const result = await getJobCompetencyMatrix(
            selectedJob.jobId,
            selectedMonth,
            selectedYear,
            myId
          );

          if (Array.isArray(result)) {
            // Safety: ensure result is an array
            const groupedObject = result.reduce((acc, curr) => {
              // 1. Destructure for readability
              const { employeeId, employeeName, photo } = curr;

              // 2. Use ID as key
              if (!acc[employeeId]) {
                acc[employeeId] = {
                  empId: employeeId,
                  empName: employeeName || "Unknown Employee", // Fallback
                  photo: photo || null,
                  competencies: [],
                };
              }

              // 3. Add the competency (curr) to the list
              acc[employeeId].competencies.push(curr);
              return acc;
            }, {});

            setData(Object.values(groupedObject));
          } else {
            setData([]);
          }
        } catch (error) {
          console.error("Matrix fetch error:", error);
          setData([]);
        } finally {
          setLoadingData(false);
        }
      } else {
        setData([]);
      }
    };

    fetchData();
  }, [selectedJob, selectedMonth, selectedYear]);

  const handleDownloadCompToEmployeeEvidence = async (compToEmpId, fileName) => {
    if (!fileName || !compToEmpId) return;

    setDownloadLoading(true);

    try {
      await downloadCompToEmployeeEvidenceData(compToEmpId, fileName);
    } catch (err) {
      console.error("Error downloading competency evidence:", err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleAdminCommentSave = async (compToEmpId, newComment) => {
    const commentPayload = {
      compToEmpId: compToEmpId,
      comment: newComment,
      commenterUid: myId, // Using me.id as requested
    };

    const result = await addAdminCompetencyEvaluationComment(commentPayload);

    // if (result !== "error") {
    //   // setPerformanceComments((prev) => {
    //   //   // Check if this comment is already in the array
    //   //   const exists = prev.find((c) => c.id === result.id);

    //   //   if (exists) {
    //   //     // UPDATE: Replace the old version with the new one from the server
    //   //     return prev.map((c) => (c.id === result.id ? result : c));
    //   //   } else {
    //   //     // INSERT: Add the new DTO to the list
    //   //     return [...prev, result];
    //   //   }
    //   // });
    // }
    if (result !== "error" && result) {
      // result should be the new comment object { id, comment, compToEmpId, ... }

      setData((prevData) =>
        prevData.map((emp) => ({
          ...emp,
          // Map through competencies to find the one that matches compToEmpId
          competencies: emp.competencies.map((comp) => {
            // Note: In your JSON, the field is 'compToEmployeeId'
            // but in the comment result it is 'compToEmpId'
            if (comp.compToEmployeeId === result.compToEmpId) {
              const existingComments = comp.adminComments || [];
              const exists = existingComments.find((c) => c.id === result.id);

              let updatedComments;
              if (exists) {
                // UPDATE: Replace the old version of the comment
                updatedComments = existingComments.map((c) => (c.id === result.id ? result : c));
              } else {
                // INSERT: Add the new comment to the list
                updatedComments = [...existingComments, result];
              }

              return {
                ...comp,
                adminComments: updatedComments,
              };
            }
            // If not the matching competency, return as is
            return comp;
          }),
        }))
      );
    }
  };

  const handleCompAssessmentReset = async () => {
    // Use the state variable (compToEmpId)
    const compToEmpIdToReset = compToReset;

    if (!compToEmpIdToReset) {
      console.error("No assessment ID provided.");
      setResetCompDialogOpen(false);
      return;
    }

    const result = await resetCompAssessment(compToEmpIdToReset);

    if (result !== "error") {
      // Update the UI state locally
      setData((prevData) =>
        prevData.map((emp) => ({
          ...emp,
          // Use .map to transform the specific competency instead of removing it
          competencies: emp.competencies.map((comp) => {
            if (comp.compToEmployeeId === compToEmpIdToReset) {
              // Return the competency object but reset the assessment fields
              return {
                ...comp,
                compToEmployeeId: null, // Reset the ID as it's deleted from DB
                achievedLevelValue: null, // Reset the score/level
                achievedLevelName: null, // Reset the level name
                adminComments: [], // Clear the comments array
                fileName: null, // Clear evidence file
                comment: null, // Clear evidence comment
              };
            }
            return comp; // Return other competencies unchanged
          }),
        }))
      );

      // Close and cleanup
      setResetCompDialogOpen(false);
      setCompToReset(null);
    } else {
      alert("Failed to reset assessment.");
      setResetCompDialogOpen(false);
    }
  };

  if (loadingData) {
    return <LoadingScreen />;
  }

  return (
    <MDBox pt={2} pb={3}>
      {/* 1. Job & Date Filtering Card */}
      <Card
        variant="outlined" // Removes the shadow and uses a border instead
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "3px", // Smooth corners
          borderColor: "#e0e3e8", // Very light/thin grey border
          borderWidth: "1px",
          boxShadow: "none", // Ensures a flat, clean look
          background: "#ffffff",
        }}
      >
        <Typography variant="h6" fontWeight="medium" mb={2}>
          {t("user.competencies.viewAssessments") || "Competency Assessment Filters"}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          {/* Job Selector */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(option) => option.jobName || ""}
              value={selectedJob}
              sx={fieldStyles}
              onChange={(event, newValue) => setSelectedJob(newValue)}
              loading={loadingJobs}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.dashboard.job")}
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingJobs ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          {/* Month Selector (25% width on desktop) */}
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              sx={fieldStyles}
              label={t("admin.dashboard.month")}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {Array.isArray(months) &&
                months.map((m, i) => (
                  <MenuItem key={m} value={i + 1}>
                    {m} {/* Shows "Jan", "Feb", etc. */}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          {/* Year Selector (25% width on desktop) */}
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              sx={fieldStyles}
              label={t("admin.dashboard.year")}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>
      {loadingData ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress color="info" />
        </Box>
      ) : (
        data.map((emp) => (
          <Box
            key={emp.empId}
            sx={{
              mb: 3,
              border: "1px solid #e0e3e8",
              borderWidth: "1px",
              boxShadow: "none", // Ensures a flat, clean look
              borderRadius: "3px",
              overflow: "hidden",
              backgroundColor: "#ffffff",
            }}
          >
            {/* Employee Header */}
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              px={3}
              py={2}
              borderBottom="1px solid #e5e7eb"
              backgroundColor="#fcfcfd"
            >
              <Avatar
                src={emp.photo ? `data:image/jpeg;base64,${emp.photo}` : undefined}
                sx={{ width: 50, height: 50, border: "1px solid #eee" }}
              >
                {emp.empName?.charAt(0)}
              </Avatar>
              <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                {emp.empName}
              </Typography>
            </Box>

            {/* Table Header Row (Hidden on Mobile) */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                px: 3,
                py: 1,
                backgroundColor: "#F6F5FA",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ flex: 1, fontSize: "12px", fontWeight: "bold", color: "#666" }}>
                {t("user.trainings.competency")?.toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  width: 180,
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#666",
                  textAlign: "center",
                }}
              >
                {t("user.trainings.currentLvl")?.toUpperCase()}
              </Typography>
            </Box>

            {/* Competency Rows */}
            {emp.competencies.map((comp) => (
              <Box
                key={comp.competencyId}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  px: 3,
                  py: 2,
                  gap: 1.5,
                  borderBottom: "1px solid #f0f0f0",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                {/* Left: Competency Name & Type */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    {comp.competencyName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("user.assess.targetLevel")}: {comp.requiredLevelName} (
                    {comp.requiredLevelValue})
                  </Typography>
                </Box>

                {/* Middle - BW Points Indicator */}
                <Box
                  sx={{
                    flex: "0 0 auto",
                    width: { xs: "100%", sm: "80px" },
                    display: "flex",
                    justifyContent: "center",
                    my: { xs: 1, sm: 0 },
                  }}
                >
                  {comp.isbwActive ? (
                    <Tooltip title="Manage Black/White Points">
                      <Chip
                        label="BW"
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                          setSelectedEmployeeForPoints({
                            empId: comp.employeeId,
                            empName: comp.employeeName,
                            compToJobId: comp.compToJobId,
                            compName: comp.competencyName,
                          });
                          setPointsDialogOpen(true);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "white",
                          },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.disabled"></Typography>
                  )}
                </Box>

                {/* Right: Level Display */}
                <Box
                  sx={{ width: { xs: "100%", sm: 220 }, textAlign: "right", mt: { xs: 1, sm: 0 } }}
                >
                  <Chip
                    label={
                      comp.achievedLevelName
                        ? `${comp.achievedLevelName} (${comp.achievedLevelValue})`
                        : t("user.assess.unassessed")
                    }
                    sx={{
                      width: "100%",
                      height: 32,
                      borderRadius: "6px",
                      fontWeight: 500,
                      fontSize: "0.8rem",

                      // Colors adjusted for MD Blue Theme
                      backgroundColor: comp.achievedLevelName ? "#E3F2FD" : "#f0f2f5",
                      // color: comp.achievedLevelName ? "#1A73E8" : "#7b809a",
                      border: `1px solid ${comp.achievedLevelName ? "#BAD9FB" : "#d2d6da"}`,

                      "& .MuiChip-label": {
                        px: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                  {/* The Evidence Download Button (Only shows if a level is achieved) */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={
                        comp.fileName
                          ? `${t("user.evidenceProvider.evidence")}: ${comp.fileName}`
                          : t("user.evidenceProvider.noEvidence")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!comp.fileName}
                          color={comp.fileName ? "primary" : "default"}
                          onClick={() =>
                            handleDownloadCompToEmployeeEvidence(
                              comp.compToEmployeeId,
                              comp.fileName
                            )
                          }
                          sx={{
                            p: 0.5,
                            backgroundColor: comp.fileName ? "#f0f7ff" : "transparent",
                            "&:hover": { backgroundColor: "#e3f2fd" },
                          }}
                        >
                          {downloadLoading ? (
                            <DownloadingIcon fontSize="small" />
                          ) : (
                            <Download fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* 2. Evidence Comment Button (BESIDE DOWNLOAD) */}
                    <EvidenceCommentButton
                      evidence={{
                        compToEmpId: comp.compToEmployeeId,
                        comment: comp.comment, // From your Java/DTO join
                      }}
                      isEditable={false} // Set to true if you want admins to be able to edit
                      disabled={!comp.fileName} // Disable if not assessed
                    />
                    <CommentButton
                      item={comp}
                      isPrimary={true} // Set to true to distinguish from the regular comment if needed
                      isAdminScorecardView={true}
                      currentUser={me}
                      allComments={comp.adminComments || []}
                      onSave={(newComment) =>
                        handleAdminCommentSave(comp.compToEmployeeId, newComment)
                      }
                      disabled={!comp.achievedLevelValue} // Disable if not assessed
                      // disabled={true}
                    />
                    {comp.achievedLevelValue && comp.compToEmployeeId && (
                      <IconButton
                        size="small"
                        title={t("admin.competency.resetAssessment")} // Ensure this key exists or use "Reset Assessment"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents row expansion if within a collapse/accordion
                          setCompToReset(comp.compToEmployeeId);
                          setResetCompDialogOpen(true);
                        }}
                        sx={{
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          color: "#ef4444", // Red color for delete action
                          "&:hover": {
                            backgroundColor: "#fef2f2",
                            borderColor: "#fecaca",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            <EmployeePointsDialog
              open={pointsDialogOpen}
              onClose={() => {
                setPointsDialogOpen(false);
                setSelectedEmployeeForPoints(null);
              }}
              employeeId={selectedEmployeeForPoints?.empId}
              compToJobId={selectedEmployeeForPoints?.compToJobId}
              employeeName={selectedEmployeeForPoints?.empName}
              competencyName={selectedEmployeeForPoints?.compName}
              snackbarRef={snackbarRef}
              canChangePoints={false}
            />
            <ConfirmationDialog
              open={resetCompDialogOpen}
              onClose={() => {
                setResetCompDialogOpen(false);
                setCompToReset(null);
              }}
              onConfirm={handleCompAssessmentReset}
              title="Reset Competency Assessment"
              message="Are you sure you want to delete this assessment, all evidence files, and all comments? This action cannot be undone."
            />
          </Box>
        ))
      )}

      {/* Empty State */}
      {!loadingData && data.length === 0 && selectedJob && (
        <Box textAlign="center" py={10}>
          <Typography variant="body1" color="textSecondary">
            {t("admin.competency.noAssessments")}
          </Typography>
        </Box>
      )}
    </MDBox>
  );
};

export default CompAssessments;
